// Digital Goods API Service for TWA Integration
import { getAuth } from "firebase/auth";
import { functionUrls } from './firebaseFunctions';

class PlayBillingService {
  constructor() {
    this.service = null;
    this.isAvailable = false;
    this.purchaseInProgress = false;
    this.retryKey = 'retryPlayPurchases';
    this.pendingKey = 'pendingPlayPurchases';

    // Clear any existing intervals from previous instances (hot reload protection)
    if (this.retryInterval) clearInterval(this.retryInterval);
    if (this.pendingInterval) clearInterval(this.pendingInterval);

    this.initializeService();
    this.setupRetryListener(); // For network retries

    // Only run initial checks if online and we haven't checked recently
    const lastCheck = localStorage.getItem('playBillingLastCheck');
    const now = Date.now();
    if (!lastCheck || (now - parseInt(lastCheck)) > 30000) { // 30 seconds
      localStorage.setItem('playBillingLastCheck', now.toString());
      if (navigator.onLine) {
        this.retryVerifications(); // Initial for network
        this.processPending(); // Initial for pending
      }
    }

    this.retryInterval = setInterval(() => {
      if (navigator.onLine) {
        this.retryVerifications();
      }
    }, 2 * 60 * 1000);
    this.pendingInterval = setInterval(() => {
      if (navigator.onLine) {
        this.processPending();
      }
    }, 2 * 60 * 1000);
  }

  async initializeService() {
    console.log('[PlayBilling] Starting service initialization');
    if ('getDigitalGoodsService' in window) {
      console.log('[PlayBilling] Digital Goods API detected in window');
      try {
        console.log('[PlayBilling] Requesting Google Play Billing service');
        this.service = await window.getDigitalGoodsService('https://play.google.com/billing');
        this.isAvailable = !!this.service;
        console.log('[PlayBilling] Service initialized successfully:', {
          isAvailable: this.isAvailable,
          serviceExists: !!this.service
        });
      } catch (error) {
        console.error('[PlayBilling] Initialization error:', error);
        console.error('[PlayBilling] Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
    } else {
      console.warn('[PlayBilling] Digital Goods API not available in window object');
    }
  }

  checkAvailability() {
    console.log('[PlayBilling] Checking availability:', this.isAvailable);
    return this.isAvailable;
  }

  async getProductDetails(productIds = ['curio_creditsot_150']) {
    console.log('[PlayBilling] Getting product details for:', productIds);
    if (!this.isAvailable || !this.service) {
      console.warn('[PlayBilling] Service not available for product details', {
        isAvailable: this.isAvailable,
        hasService: !!this.service
      });
      return null;
    }
    try {
      console.log('[PlayBilling] Fetching details from service');
      const details = await this.service.getDetails(productIds);
      console.log('[PlayBilling] Raw product details received:', details);

      const mappedDetails = details.map(item => ({
        itemId: item.itemId,
        title: item.title,
        description: item.description,
        price: item.price,
        iconUrl: item.iconUrl,
        type: item.type,
        subscriptionPeriod: item.subscriptionPeriod,
        freeTrialPeriod: item.freeTrialPeriod,
        introductoryPrice: item.introductoryPrice,
        introductoryPricePeriod: item.introductoryPricePeriod
      }));

      console.log('[PlayBilling] Mapped product details:', mappedDetails);
      return mappedDetails;
    } catch (error) {
      console.error('[PlayBilling] Error getting product details:', error);
      console.error('[PlayBilling] Product details error context:', {
        productIds,
        errorMessage: error.message,
        errorName: error.name
      });
      return null;
    }
  }

  async initiatePurchase(productId = 'curio_creditsot_150') {
    console.log('[PlayBilling] Initiating purchase for product:', productId);

    if (!this.isAvailable || !this.service) {
      console.error('[PlayBilling] Cannot initiate purchase - service not available', {
        isAvailable: this.isAvailable,
        hasService: !!this.service
      });
      throw new Error('Digital Goods API not available');
    }

    if (this.purchaseInProgress) {
      console.warn('[PlayBilling] Purchase already in progress, blocking new request');
      throw new Error('Purchase already in progress');
    }

    console.log('[PlayBilling] Setting purchase in progress flag');
    this.purchaseInProgress = true;

    try {
      const paymentMethods = [{
        supportedMethods: 'https://play.google.com/billing',
        data: { sku: productId }
      }];
      const paymentDetails = {
        total: { label: 'Total', amount: { currency: 'USD', value: '0' } }
      };

      console.log('[PlayBilling] Creating PaymentRequest with:', {
        productId,
        paymentMethods,
        paymentDetails
      });

      const request = new PaymentRequest(paymentMethods, paymentDetails);
      console.log('[PlayBilling] Showing payment UI');
      const paymentResponse = await request.show();

      console.log('[PlayBilling] Payment response received:', {
        details: paymentResponse.details,
        methodName: paymentResponse.methodName,
        payerName: paymentResponse.payerName
      });

      const { purchaseToken, orderId } = paymentResponse.details;
      console.log('[PlayBilling] Completing payment with success status');
      await paymentResponse.complete('success');

      this.purchaseInProgress = false;
      const purchaseData = { purchaseToken, orderId, productId, purchaseTime: Date.now() };
      console.log('[PlayBilling] Purchase completed successfully:', purchaseData);
      return purchaseData;
    } catch (error) {
      this.purchaseInProgress = false;

      if (error.name === 'AbortError') {
        console.log('[PlayBilling] Purchase cancelled by user');
        throw new Error('Purchase cancelled');
      }

      console.error('[PlayBilling] Purchase error:', error);
      console.error('[PlayBilling] Purchase error details:', {
        productId,
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack
      });
      throw error;
    }
  }

  async verifyPurchase(purchaseData) {
    console.log('[PlayBilling] Starting purchase verification:', purchaseData);

    if (!purchaseData || !purchaseData.purchaseToken) {
      console.error('[PlayBilling] Invalid purchase data provided:', purchaseData);
      throw new Error('Invalid purchase data');
    }

    const auth = getAuth();
    let idToken;
    if (auth.currentUser) {
      console.log('[PlayBilling] Getting ID token for user:', auth.currentUser.uid);
      idToken = await auth.currentUser.getIdToken();
      console.log('[PlayBilling] ID token obtained successfully');
    } else {
      console.warn('[PlayBilling] No authenticated user for purchase verification');
    }

    if (!functionUrls?.verifyPlayPurchase) {
      console.error('[PlayBilling] Verification URL not configured in functionUrls');
      throw new Error('Play purchase verification URL not configured');
    }

    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;

    const purchaseType = purchaseData.productId?.includes('subscription') ? 'subscription' : 'product';
    const requestBody = {
      ...purchaseData,
      purchaseType
    };

    console.log('[PlayBilling] Sending verification request:', {
      url: functionUrls.verifyPlayPurchase,
      purchaseType,
      productId: purchaseData.productId,
      hasToken: !!purchaseData.purchaseToken
    });

    try {
      const response = await fetch(functionUrls.verifyPlayPurchase, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      console.log('[PlayBilling] Verification response status:', response.status);
      const data = await response.json();
      console.log('[PlayBilling] Verification response data:', data);

      if (!response.ok) {
        console.error('[PlayBilling] Verification failed with error:', data.error);
        throw new Error(data.error || 'Purchase verification failed');
      }

      if (response.status === 202) {
        console.log('[PlayBilling] Payment is pending (backend response) - storing for polling');
        data.isPending = true;
        this.storeForPending(purchaseData);
        // Will be retried by the 2-minute interval
      }

      console.log('[PlayBilling] Purchase verified successfully:', data);
      return data;
    } catch (error) {
      console.error('[PlayBilling] Verification request failed:', error);
      console.error('[PlayBilling] Verification error details:', {
        errorMessage: error.message,
        errorName: error.name,
        purchaseData
      });
      if (error.message === 'Failed to fetch' || !navigator.onLine) {
        console.warn('[PlayBilling] Network failure during verification - storing for retry');
        this.storeForRetry(purchaseData);
        throw new Error('Network error - purchase will be verified when connection is restored');
      }
      throw error;
    }
  }

  async resolvePendingPayment(purchaseData) {
    console.log('[PlayBilling] Resolving pending payment using verifyPurchase:', purchaseData);

    // Simply use verifyPurchase for resolving pending payments
    try {
      const result = await this.verifyPurchase(purchaseData);
      console.log('[PlayBilling] Payment resolved successfully:', result);
      return result;
    } catch (error) {
      console.error('[PlayBilling] Failed to resolve pending payment:', error);
      throw error;
    }
  }

  storeForRetry(purchaseData) {
    let retries = JSON.parse(localStorage.getItem(this.retryKey)) || [];
    if (!retries.some(p => p.purchaseToken === purchaseData.purchaseToken)) {
      retries.push(purchaseData);
      localStorage.setItem(this.retryKey, JSON.stringify(retries));
      console.log('[PlayBilling] Stored purchase for retry:', purchaseData.purchaseToken);
    }
  }

  storeForPending(purchaseData) {
    let pendings = JSON.parse(localStorage.getItem(this.pendingKey)) || [];
    if (!pendings.some(p => p.purchaseToken === purchaseData.purchaseToken)) {
      pendings.push(purchaseData);
      localStorage.setItem(this.pendingKey, JSON.stringify(pendings));
      console.log('[PlayBilling] Stored pending payment for polling:', purchaseData.purchaseToken);
    }
  }

  async retryVerifications() {
    if (!navigator.onLine) return;

    let retries = JSON.parse(localStorage.getItem(this.retryKey)) || [];
    if (retries.length === 0) return;

    console.log('[PlayBilling] Retrying', retries.length, 'verifications due to network issues');

    const newRetries = [];
    for (const data of retries) {
      try {
        await this.verifyPurchase(data);
        console.log('[PlayBilling] Successfully verified purchase on retry:', data.purchaseToken);
      } catch (error) {
        if (error.message.includes('Network error')) {
          newRetries.push(data);
        } else {
          console.error('[PlayBilling] Failed to verify purchase on retry:', error);
          // Depending on error, decide whether to keep or remove
          if (!error.message.includes('cancelled')) {
            newRetries.push(data);
          }
        }
      }
    }

    localStorage.setItem(this.retryKey, JSON.stringify(newRetries));
  }

  async processPending() {
    if (!navigator.onLine) return;

    let pendings = JSON.parse(localStorage.getItem(this.pendingKey)) || [];
    if (pendings.length === 0) return;

    console.log('[PlayBilling] Polling', pendings.length, 'pending payments');

    const newPendings = [];
    for (const data of pendings) {
      try {
        await this.resolvePendingPayment(data);
        console.log('[PlayBilling] Successfully resolved pending payment:', data.purchaseToken);
      } catch (error) {
        if (error.message.includes('still pending') || error.message.includes('Network error')) {
          newPendings.push(data);
        } else if (error.message.includes('cancelled')) {
          console.log('[PlayBilling] Removing cancelled payment from pending:', data.purchaseToken);
          // Don't push to newPendings
        } else {
          console.error('[PlayBilling] Failed to resolve pending payment:', error);
          newPendings.push(data);
        }
      }
    }

    localStorage.setItem(this.pendingKey, JSON.stringify(newPendings));
  }

  setupRetryListener() {
    window.addEventListener('online', () => {
      console.log('[PlayBilling] Connection restored, retrying network verifications');
      this.retryVerifications();
    });
  }

  cleanup() {
    console.log('[PlayBilling] Cleaning up intervals and listeners');
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
    if (this.pendingInterval) {
      clearInterval(this.pendingInterval);
      this.pendingInterval = null;
    }
    // Remove the online listener if needed
    // window.removeEventListener('online', ...); // Would need to store the function reference
  }

  async restorePurchases() {
    console.log('[PlayBilling] Starting purchase restoration');

    if (!this.isAvailable || !this.service) {
      console.warn('[PlayBilling] Cannot restore purchases - service not available', {
        isAvailable: this.isAvailable,
        hasService: !!this.service
      });
      return [];
    }

    try {
      console.log('[PlayBilling] Listing all purchases from service');
      const purchases = await this.service.listPurchases();
      console.log('[PlayBilling] Raw purchases list:', purchases);

      const unconsumedPurchases = purchases
        .filter(p => !p.consumed)
        .map(p => ({
          purchaseToken: p.purchaseToken,
          productId: p.itemId,
          acknowledged: p.acknowledged
        }));

      console.log('[PlayBilling] Unconsumed purchases found:', unconsumedPurchases);
      console.log('[PlayBilling] Purchase restoration summary:', {
        totalPurchases: purchases.length,
        unconsumedCount: unconsumedPurchases.length
      });

      return unconsumedPurchases;
    } catch (error) {
      console.error('[PlayBilling] Error restoring purchases:', error);
      console.error('[PlayBilling] Restore error details:', {
        errorMessage: error.message,
        errorName: error.name
      });
      return [];
    }
  }

  isTWA() {
    const referrer = document.referrer;
    const hasAndroidReferrer = referrer.startsWith('android-app://');
    const hasTWAParam = new URLSearchParams(window.location.search).has('twa');
    const hasDigitalGoodsAPI = 'getDigitalGoodsService' in window;

    const isTWA = hasAndroidReferrer || hasTWAParam || hasDigitalGoodsAPI;

    console.log('[PlayBilling] TWA detection:', {
      referrer,
      hasAndroidReferrer,
      hasTWAParam,
      hasDigitalGoodsAPI,
      isTWA
    });

    return isTWA;
  }

  async testPurchase() {
    console.log('[PlayBilling] Test purchase initiated');

    if (!this.isTWA()) {
      const testData = {
        purchaseToken: 'test_token_' + Date.now(),
        orderId: 'test_order_' + Date.now(),
        productId: 'curio_creditsot_150',
        purchaseTime: Date.now(),
        test: true
      };
      console.log('[PlayBilling] Not in TWA, returning test purchase data:', testData);
      return testData;
    }

    console.log('[PlayBilling] In TWA environment, initiating real purchase');
    return this.initiatePurchase('curio_creditsot_150');
  }
}

// Singleton pattern to ensure only one instance exists
let playBillingInstance = null;

const playBillingService = (() => {
  if (!playBillingInstance) {
    playBillingInstance = new PlayBillingService();
  }
  return playBillingInstance;
})();

export default playBillingService;